import { jsonify, sanitize } from "../helpers";
const DEFAULT_GET_ALL_OPTIONS = {
  perPage: 50,
  maxConcurrent: 4,
  getPageFromResponse: res => res.data,
  getTotalCountFromResponse: res => res.total_count,
};
export default class ScribbleClient {
  constructor(raw) {
    this.raw = raw;
  }
  /**
   * Create one or more new Annotations in a project.
   * @note
   * POST request to `/projects/{project_uid}/annotations2_/create`
   */
  batchCreateAnnotation(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2_/create`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/annotations2/links_/create`
   */
  batchCreateAnnotationLink(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2/links_/create`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Delete multiple Annotations in a project.
   * @note
   * POST request to `/projects/{project_uid}/annotations2_/delete`
   */
  batchDeleteAnnotation(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/annotations2/{annotation_uid}/links_/delete`
   */
  batchDeleteAnnotationLinks(projectUid, annotationUid, body) {
    const url = `/projects/${projectUid}/annotations2/${annotationUid}/links_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Delete multiple calibrations in a project.
   * @note
   * POST request to `/projects/{project_uid}/calibrations_/delete`
   */
  batchDeleteCalibration(projectUid, body) {
    const url = `/projects/${projectUid}/calibrations_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/layers_/delete`
   */
  batchDeleteLayer(projectUid, body) {
    const url = `/projects/${projectUid}/layers_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/surfaces_/delete`
   */
  batchDeleteSurface(projectUid, body) {
    const url = `/projects/${projectUid}/surfaces_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create one or more new Annotation in a project.
   * @note
   * POST request to `/projects/{project_uid}/annotations2_/update`
   */
  batchUpdateAnnotation(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2_/update`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * GET request to `/projects/{project_uid}/scribble_uploads/completions/{token}`
   */
  completeUpload(projectUid, token) {
    const url = `/projects/${projectUid}/scribble_uploads/completions/${token}`;
    return this.raw.get(url);
  }
  /**
   * Copy annotations to a different layer.
   * The purpose of this endpoint is to let bebop promote specific user-edited
   * annotations. The copied annotations are preserved in terms of deletion and
   * created_by user. The endpoint is restricted to super users.
   * @note
   * POST request to `/projects/{project_uid}/annotations2_/copy`
   */
  copyAnnotations(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2_/copy`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new Annotation in a project.
   * @note
   * POST request to `/projects/{project_uid}/annotations2`
   */
  createAnnotation(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/annotations2/{annotation_uid}/links`
   */
  createAnnotationLink(projectUid, annotationUid, body) {
    const url = `/projects/${projectUid}/annotations2/${annotationUid}/links`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new Calibration in a project.
   * @note
   * POST request to `/projects/{project_uid}/calibrations`
   */
  createCalibration(projectUid, body) {
    const url = `/projects/${projectUid}/calibrations`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/scribble_jobs`
   */
  createJob(projectUid, body) {
    const url = `/projects/${projectUid}/scribble_jobs`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new Layer in a project.
   * @note
   * POST request to `/projects/{project_uid}/layers`
   */
  createLayer(projectUid, body) {
    const url = `/projects/${projectUid}/layers`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/stamp_images`
   */
  createStampImage(body) {
    const url = "/stamp_images";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new Surface in a project.
   * @note
   * POST request to `/projects/{project_uid}/surfaces`
   */
  createSurface(projectUid, body) {
    const url = `/projects/${projectUid}/surfaces`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/scribble_uploads`
   */
  createUpload(projectUid, body) {
    const url = `/projects/${projectUid}/scribble_uploads`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Given an entity (currently only accepts photos), find all links
   * (on all versions of all sheets) that point to the entity
   * (link.destination == destination_uid) and soft delete them.
   * See https://jira.autodesk.com/browse/AV2-2153.
   * This method should only be called by service endpoints (only photos for now).
   * @note
   * POST request to `/projects/{project_uid}}/annotations2/links_/delete_all_references_to`
   */
  deleteAllReferencesTo(projectUid, body) {
    const url = `/projects/${projectUid}}/annotations2/links_/delete_all_references_to`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * DELETE request to `/projects/{project_uid}/annotations2/{uid}`
   */
  deleteAnnotation(projectUid, uid) {
    const url = `/projects/${projectUid}/annotations2/${uid}`;
    return this.raw.delete(url);
  }
  /**
   * @note
   * DELETE request to `/projects/{project_uid}/annotations2/{annotation_uid}/links/{link_uid}`
   */
  deleteAnnotationLink(projectUid, annotationUid, linkUid) {
    const url = `/projects/${projectUid}/annotations2/${annotationUid}/links/${linkUid}`;
    return this.raw.delete(url);
  }
  /**
   * @note
   * DELETE request to `/annotation_templates/{template_uid}`
   */
  deleteAnnotationTemplate(templateUid) {
    const url = `/annotation_templates/${templateUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete a single calibration in a project.
   * @note
   * DELETE request to `/projects/{project_uid}/calibrations/{uid}`
   */
  deleteCalibration(projectUid, uid) {
    const url = `/projects/${projectUid}/calibrations/${uid}`;
    return this.raw.delete(url);
  }
  /**
   * @note
   * DELETE request to `/projects/{project_uid}/layers/{uid}`
   */
  deleteLayer(projectUid, uid) {
    const url = `/projects/${projectUid}/layers/${uid}`;
    return this.raw.delete(url);
  }
  /**
   * @note
   * DELETE request to `/stamp_images/{stamp_image_uid}`
   */
  deleteStampImage(stampImageUid) {
    const url = `/stamp_images/${stampImageUid}`;
    return this.raw.delete(url);
  }
  /**
   * @note
   * DELETE request to `/projects/{project_uid}/surfaces/{uid}`
   */
  deleteSurface(projectUid, uid) {
    const url = `/projects/${projectUid}/surfaces/${uid}`;
    return this.raw.delete(url);
  }
  /**
   * Retrieve one Annotation in a project.
   * @note
   * GET request to `/projects/{project_uid}/annotations2/{uid}`
   */
  getAnnotation(projectUid, uid) {
    const url = `/projects/${projectUid}/annotations2/${uid}`;
    return this.raw.get(url);
  }
  getAnnotationTemplates(params, options = { getAll: false }) {
    const url = "/annotation_templates";
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getBaseEntityAnnotations(
    projectUid,
    baseEntityUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/base_entity/${baseEntityUid}/annotations2`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Uses the sequence numbers for the latest version of each data model returned for this query to generate
   * a hashed string that can be used to determine if a cache of this query is out of date or not.
   * :param project_uid: The project
   * :param base_entity_uid: The sheet or document id.
   * :return: The hashed string.
   * @note
   * GET request to `/projects/{project_uid}/base_entity/{base_entity_uid}/cache_hash`
   */
  getBaseEntityCacheHash(projectUid, baseEntityUid, params) {
    const url = {
      url: `/projects/${projectUid}/base_entity/${baseEntityUid}/cache_hash`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  getBaseEntityLayers(
    projectUid,
    baseEntityUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/base_entity/${baseEntityUid}/layers`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns the number of user and published markups for a set of sheets or documents passed in for the current user.
   * :param project_uid: The project the surface is part of.
   * :return: The job details.
   * @note
   * POST request to `/projects/{project_uid}/base_entity_/stats`
   */
  getBaseEntityStats(projectUid, body) {
    const url = `/projects/${projectUid}/base_entity_/stats`;
    return this.raw.post(url, jsonify(body));
  }
  getBaseEntitySurfaces(
    projectUid,
    baseEntityUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/base_entity/${baseEntityUid}/surfaces`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/annotations2_/get`
   */
  getBatchAnnotations(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2_/get`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Retrieve one Calibration in a project.
   * @note
   * GET request to `/projects/{project_uid}/calibrations/{uid}`
   */
  getCalibration(projectUid, uid) {
    const url = `/projects/${projectUid}/calibrations/${uid}`;
    return this.raw.get(url);
  }
  getFeatureBoundAnnotations(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/annotations2`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Return the history of a given surface.
   * Returns an array of surface UIDs, including <surface_uid>.
   * @note
   * GET request to `/projects/{project_uid}/surfaces/{surface_uid}/history`
   */
  getHistory(projectUid, surfaceUid) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/history`;
    return this.raw.get(url);
  }
  /**
   * @note
   * GET request to `/projects/{project_uid}/scribble_jobs/{job_uid}`
   */
  getJob(projectUid, jobUid, params) {
    const url = {
      url: `/projects/${projectUid}/scribble_jobs/${jobUid}`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Retrieve one Layer in a project.
   * @note
   * GET request to `/projects/{project_uid}/layers/{uid}`
   */
  getLayer(projectUid, uid) {
    const url = `/projects/${projectUid}/layers/${uid}`;
    return this.raw.get(url);
  }
  getLayerAnnotations(
    projectUid,
    layerUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/layers/${layerUid}/annotations2`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getStampImages(params, options = { getAll: false }) {
    const url = "/stamp_images";
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * @note
   * GET request to `/stencil_meta_override_schemas`
   */
  getStencilMetaOverrideSchemas() {
    const url = "/stencil_meta_override_schemas";
    return this.raw.get(url);
  }
  /**
   * @note
   * GET request to `/stencil_meta_schemas`
   */
  getStencilMetaSchemas() {
    const url = "/stencil_meta_schemas";
    return this.raw.get(url);
  }
  /**
   * DEPRECATED - use /projects/(project_uid)/base_entity/(surface_uid)/surfaces.
   * @note
   * GET request to `/projects/{project_uid}/surfaces/{uid}`
   */
  getSurface(projectUid, uid) {
    const url = `/projects/${projectUid}/surfaces/${uid}`;
    return this.raw.get(url);
  }
  getSurfaceAnnotations(
    projectUid,
    surfaceUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/annotations2`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getSurfaceLayers(
    projectUid,
    surfaceUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/layers`;
    if (options.getAll) {
      return this.raw.getAll(
        { url, params: sanitize(params || {}, {}) },
        { ...DEFAULT_GET_ALL_OPTIONS, ...options }
      );
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns the number of user and published markups for a set of sheets passed in for the current user.
   * :param project_uid: The project the surface is part of.
   * :return: The job details.
   * @note
   * POST request to `/projects/{project_uid}/surfaces_/stats`
   */
  getSurfaceStats(projectUid, body) {
    const url = `/projects/${projectUid}/surfaces_/stats`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Queues a surface for promotion.  This endpoint is restricted to super users.
   * :param project_uid: The project the surface is part of.
   * :param surface_uid: The surface we are promoting.
   * :return: The job details.
   * @note
   * POST request to `/projects/{project_uid}/surfaces/{surface_uid}/promote`
   */
  promoteSurface(projectUid, surfaceUid, body) {
    const url = `/projects/${projectUid}/surfaces/${surfaceUid}/promote`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Restore deleted annotation
   * @note
   * POST request to `/projects/{project_uid}/annotations2/{uid}/restore`
   */
  restoreAnnotation(projectUid, uid, body) {
    const url = `/projects/${projectUid}/annotations2/${uid}/restore`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/annotations2_/search`
   */
  searchAnnotations(projectUid, body, params) {
    const url = {
      url: `/projects/${projectUid}/annotations2_/search`,
      params: sanitize(params || {}),
    };
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Sync annotations for a project.
   * @note
   * GET request to `/projects/{project_uid}/annotations2_/sync`
   */
  syncAnnotations(projectUid, params) {
    const url = {
      url: `/projects/${projectUid}/annotations2_/sync`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Sync layers for a project.
   * @note
   * GET request to `/projects/{project_uid}/layers_/sync`
   */
  syncLayers(projectUid, params) {
    const url = {
      url: `/projects/${projectUid}/layers_/sync`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * @note
   * POST request to `/projects/{project_uid}/annotations2:sync-status`
   */
  syncStatus(projectUid, body) {
    const url = `/projects/${projectUid}/annotations2:sync-status`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Sync surfaces for a project.
   * @note
   * GET request to `/projects/{project_uid}/surfaces_/sync`
   */
  syncSurfaces(projectUid, params) {
    const url = {
      url: `/projects/${projectUid}/surfaces_/sync`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Update an Annotation in a project.
   * This will perform an "upsert" if the Annotation with the
   * uid does not exist.
   * @note
   * PUT request to `/projects/{project_uid}/annotations2/{uid}`
   */
  updateAnnotation(projectUid, uid, body) {
    const url = `/projects/${projectUid}/annotations2/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * Update a/n Calibration in a project.
   * This will perform an "upsert" if the Calibration with the
   * uid does not exist.
   * @note
   * PUT request to `/projects/{project_uid}/calibrations/{uid}`
   */
  updateCalibration(projectUid, uid, body) {
    const url = `/projects/${projectUid}/calibrations/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * Update a/n Layer in a project.
   * This will perform an "upsert" if the Layer with the
   * uid does not exist.
   * @note
   * PUT request to `/projects/{project_uid}/layers/{uid}`
   */
  updateLayer(projectUid, uid, body) {
    const url = `/projects/${projectUid}/layers/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * Update a/n Surface in a project.
   * This will perform an "upsert" if the Surface with the
   * uid does not exist.
   * @note
   * PUT request to `/projects/{project_uid}/surfaces/{uid}`
   */
  updateSurface(projectUid, uid, body) {
    const url = `/projects/${projectUid}/surfaces/${uid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * @note
   * PUT request to `/annotation_templates/{template_uid}`
   */
  upsertAnnotationTemplate(templateUid, body) {
    const url = `/annotation_templates/${templateUid}`;
    return this.raw.put(url, jsonify(body));
  }
}
